<template>
    <div
        v-if="isError"
        class="box-body with-border clearfix"
    >
        <div class="row">
            <div class="col-xs-12">
                <p>
                    {{ $t('messages.api.tag.noTag', { id: tagId }) }}
                </p>
            </div>
        </div>
    </div>

    <ValidationObserver
        v-else
        v-slot="{ invalid }"
        ref="observer"
        tag="div"
    >
        <div class="box-body">
            <div class="row">
                <div class="col-xs-12 col-sm-6 col-lg-4">
                    <ValidationProvider
                        v-slot="{ errors }"
                        name="name"
                        rules="required"
                        slim
                    >
                        <div
                            class="form-group"
                            :class="{ 'has-error': errors.length }"
                        >
                            <label>{{ $t('name') }} <b class="text-danger">*</b></label>

                            <input
                                v-model="tag.name"
                                type="text"
                                class="form-control"
                                :placeholder="`${$t('enter')} ${$t('name')}`"
                                :disabled="isLoading"
                            />

                            <div
                                v-if="errors.length"
                                class="help-block"
                                v-text="errors[0]"
                            />
                        </div>
                    </ValidationProvider>
                </div>
            </div>
        </div>

        <div class="box-footer">
            <div class="row">
                <div class="col-xs-8">
                    <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        :disabled="isLoading || invalid"
                        @click="submitTag"
                    >
                        <i
                            v-if="isLoading"
                            class="fa fa-refresh fa-spin"
                        />
                        <span>{{ isEdit ? 'Update' : 'Create' }}</span>
                    </button>

                    <button
                        class="btn btn-sm btn-default"
                        :disabled="isLoading"
                        @click="
                            $router.push({
                                name: 'apis.tags.list',
                            })
                        "
                    >
                        {{ $t('cancel') }}
                    </button>
                </div>

                <div
                    v-if="isEdit"
                    class="col-xs-4 text-right"
                >
                    <button
                        class="btn btn-sm btn-danger btn-xs-block"
                        type="button"
                        data-toggle="modal"
                        data-target="#delete-modal"
                        :disabled="isLoading"
                    >
                        {{ $t('delete') }}
                    </button>
                </div>
            </div>
        </div>

        <ApisTagsModalDelete
            :tag="tag"
            @deleted="onDeleted"
        />
    </ValidationObserver>
</template>

<script>
import EventBus from '@/event-bus';
import ApisTagsModalDelete from '@/components/apis/tags/ApisTagsModalDelete';

export default {
    components: {
        ApisTagsModalDelete,
    },
    props: {
        tagId: {
            type: Number,
            default: undefined,
        },
    },
    data() {
        return {
            isError: false,
            isLoading: false,
            tag: {
                name: '',
                apis: [],
            },
        };
    },
    computed: {
        isEdit() {
            return !!this.tagId;
        },
    },
    async created() {
        if (this.isEdit) {
            await this.fetchApiTag();
        }

        this.$emit('loaded', this.tag);
    },

    methods: {
        async fetchApiTag() {
            try {
                this.isLoading = true;

                const {
                    data: { tag },
                } = await axios.get(`/apis/tags/data/${this.tagId}`);

                this.tag = tag;
            } catch (error) {
                this.isError = true;
            } finally {
                this.isLoading = false;
            }
        },

        async submitTag() {
            if (!(await this.$refs.observer.validate())) {
                return;
            }

            try {
                this.isLoading = true;

                const url = this.isEdit ? `/apis/tags/data/${this.tagId}` : '/apis/tags/data';

                const {
                    data: { action, tag },
                } = await axios({
                    method: this.isEdit ? 'PUT' : 'POST',
                    url,
                    data: this.tag,
                });

                if (action === 'created') {
                    this.onCreated(tag.name);
                } else if (action === 'updated') {
                    this.onUpdated(tag.id, tag.name);
                }
            } catch (error) {
                const {
                    data: { errors, message },
                } = error.response;

                if (errors) {
                    this.$refs.observer.setErrors(errors);
                }

                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message,
                    errors,
                });
            } finally {
                this.isLoading = false;
            }
        },

        async onDeleted() {
            return await this.$router.replace({
                name: 'apis.tags.list',
            });
        },

        async onUpdated(id, name) {
            if (!id && !name) return;

            await this.$router.push({
                name: 'apis.tags.show',
                id,
            });

            EventBus.$emit('alertData', {
                class: 'alert-success',
                message: this.$t('messages.api.tag.updated', { name }),
            });
        },

        async onCreated(name) {
            if (!name) return;

            await this.$router.push({
                name: 'apis.tags.list',
            });

            EventBus.$emit('alertData', {
                class: 'alert-success',
                message: this.$t('messages.api.tag.created', { name }),
            });
        },
    },
};
</script>
